import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useAppScopeStore } from '@frontend/scope';
import { useFeatureSubscriptionConfig } from '@frontend/self-upgrade';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';

type UseIsCallIntelEligibleAccountParams = {
  includeAiReceptionist?: boolean;
  locationIds?: string[];
};

type UseIsCIEligibleAccount = {
  isCallIntelActive: boolean;
  isCallIntelEligibleAccount: boolean;
  isCallIntelUpgradeEligible: boolean;
  isCallIntelFreeTrialEligible: boolean;
  activeLocationIds: string[];
  isLoading: boolean;
};

const FEATURE_ACTIVE_STATES = [State.ACTIVE, State.PROMOTE];

// This is used in the Analytics app to determine if the location is eligible for the call intel feature.
export const useIsCallIntelEligibleAccount = ({
  includeAiReceptionist = true,
  locationIds,
}: UseIsCallIntelEligibleAccountParams = {}): UseIsCIEligibleAccount => {
  const { selectedLocationIds: appScopeLocationIds } = useAppScopeStore();
  const selectedLocationIds = locationIds || appScopeLocationIds;
  const { locationFlagMap: voiceAiLocationFlagMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const {
    isFeatureActiveInSomeLocations: isCallIntelFeatureActive,
    isUpgradeOrTrialEligible: isCallIntelUpgradeEligible = false,
    isFreeTrialEligible: isCallIntelFreeTrialEligible = false,
    subscriptionDataList: callIntelSubscriptionData,
    isLoading: isCallIntelLoading,
  } = useFeatureSubscriptionConfig({
    activeStates: FEATURE_ACTIVE_STATES,
    checkBundleUpgrade: false,
    feature: Feature.CALL_INTELLIGENCE,
    locationIds: selectedLocationIds,
  });

  // Get locations with Voice AI customization flags active
  const voiceAiActiveLocations = useMemo(() => {
    if (!includeAiReceptionist || !selectedLocationIds || selectedLocationIds.length === 0) {
      return [];
    }

    return selectedLocationIds.filter((locationId) => voiceAiLocationFlagMap[locationId]?.state === State.ACTIVE);
  }, [includeAiReceptionist, selectedLocationIds, voiceAiLocationFlagMap]);

  const isVoiceAiFeatureActive = voiceAiActiveLocations.length > 0;

  const activeLocationIds = useMemo(() => {
    const callIntelLocations =
      callIntelSubscriptionData?.filter(({ isStateActive }) => isStateActive).map(({ locationId }) => locationId) || [];

    if (!includeAiReceptionist) {
      return callIntelLocations;
    }

    // Combine Call Intelligence and Voice AI locations
    const combinedLocationIds = [...callIntelLocations, ...voiceAiActiveLocations];
    return [...new Set(combinedLocationIds)];
  }, [callIntelSubscriptionData, includeAiReceptionist, voiceAiActiveLocations]);

  const isCallIntelActive = !includeAiReceptionist
    ? isCallIntelFeatureActive
    : isCallIntelFeatureActive || isVoiceAiFeatureActive;

  const isCallIntelEligibleAccount = isCallIntelFeatureActive || isCallIntelUpgradeEligible;

  return {
    isCallIntelActive,
    isCallIntelEligibleAccount,
    isCallIntelUpgradeEligible,
    isCallIntelFreeTrialEligible,
    activeLocationIds,
    isLoading: isCallIntelLoading,
  };
};
